import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';
import {
  Observable,
  catchError,
  combineLatest,
  from,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';

import {
  UserWorkspacesScope,
  injectFetchUserWorkspaces,
  userWorkspacesQueriesFilter,
} from 'src/app/api/queries/workspace.queries';
import { AppState } from 'src/app/reducers';

import { Workspace } from '../models/core.models';
import {
  selectCurrentCompanyId,
  selectHomeObjectsLifeCycle,
  selectUser,
} from '../reducers';

/**
 * The user's workspace list for every consumer that is not the home screen - the
 * workspace pickers and `WorkspaceResolver`.
 *
 * They read through the imperative `fetchQuery` tier with `staleTime: Infinity`:
 * serve the cache when it holds a list, fetch only when it does not. That keeps the
 * home screen the one and only reactive observer of the key, which is what stops a
 * picker - which knows nothing about the stats view option - from refetching the
 * list without stats while home is rendering it.
 *
 * A service rather than hooks because `WorkspaceResolver` is not a component and the
 * pickers only need a one-shot list for a `<select>`.
 */
@Injectable({ providedIn: 'root' })
export class UserWorkspacesService {
  private readonly store = inject(Store<AppState>);
  private readonly queryClient = inject(QueryClient);
  private readonly fetchWorkspaces = injectFetchUserWorkspaces();

  /** All of the user's workspaces for the current lifecycle status, sorted by title. */
  sortedWorkspaces$(): Observable<Workspace[]> {
    return this.workspaces$().pipe(
      map((workspaces) =>
        [...workspaces].sort((a, b) => a.title.localeCompare(b.title)),
      ),
    );
  }

  /** One workspace by id, or null when it is not in the current list. */
  workspaceById$(workspaceId: string): Observable<Workspace | null> {
    return this.workspaces$().pipe(
      map((workspaces) => workspaces.find((w) => w.id === workspaceId) ?? null),
    );
  }

  /** Drop every cached workspace list, whatever its lifecycle status. */
  invalidate(): void {
    void this.queryClient.invalidateQueries(userWorkspacesQueriesFilter());
  }

  private workspaces$(): Observable<Workspace[]> {
    return combineLatest([
      this.store.select(selectUser),
      this.store.select(selectCurrentCompanyId),
      this.store.select(selectHomeObjectsLifeCycle),
    ]).pipe(
      take(1),
      map(([user, companyId, lifecycleStatus]): UserWorkspacesScope | null =>
        user?.id
          ? { userId: user.id, companyId, lifecycleStatus, withStats: false }
          : null,
      ),
      switchMap((scope) =>
        scope ? from(this.fetchWorkspaces(scope)) : of([]),
      ),
      // * a failed fetch degrades to an empty list rather than erroring the stream:
      // * these consumers are a `<select>`'s options and a route resolver, and an
      // * error would kill the picker or cancel the navigation
      catchError(() => of([] as Workspace[])),
    );
  }
}
